import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import { route } from 'preact-router';

import lockScreenStore from '../../stores/lockScreen';
import Avatar from '../gui/avatar';

const fullNameOf = (u) => `${u?.firstname || ''} ${u?.lastname || ''}`.trim() || '?';

@observer
class LockScreen extends Component {
    state = {
        pin: '',
        submitting: false,
        isMobile: typeof window !== 'undefined' && window.innerWidth < 576,
    };

    componentDidMount() {
        if (typeof window !== 'undefined') {
            window.addEventListener('keydown', this.onKeyDown);
            window.addEventListener('resize', this.onResize);
        }
    }

    componentWillUnmount() {
        if (typeof window !== 'undefined') {
            window.removeEventListener('keydown', this.onKeyDown);
            window.removeEventListener('resize', this.onResize);
        }
    }

    onResize = () => {
        const isMobile = window.innerWidth < 576;
        if (isMobile !== this.state.isMobile) {
            this.setState({ isMobile });
        }
    }

    onKeyDown = (e) => {
        if (!lockScreenStore.isLocked) return;
        if (this.state.submitting) return;
        // Roster-level shortcut: Escape backs out from keypad
        if (e.key === 'Escape' && lockScreenStore.selectedUserId) {
            e.preventDefault();
            lockScreenStore.selectUser(null);
            this.setState({ pin: '' });
            return;
        }
        // Keypad-only shortcuts: digits, Backspace, Enter
        if (!lockScreenStore.selectedUserId) return;
        if (e.key >= '0' && e.key <= '9') {
            e.preventDefault();
            this.onPinDigit(e.key);
        } else if (e.key === 'Backspace') {
            e.preventDefault();
            this.onBackspace();
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (this.state.pin.length === 4) this.submit();
        }
    }

    selectUser = (userId) => {
        lockScreenStore.selectUser(userId);
        this.setState({ pin: '', submitting: false });
    }

    onPinDigit = (digit) => {
        const next = (this.state.pin + String(digit)).slice(0, 4);
        this.setState({ pin: next }, () => {
            if (next.length === 4) this.submit();
        });
    }

    onBackspace = () => {
        this.setState({ pin: this.state.pin.slice(0, -1) });
    }

    submit = async () => {
        const { pin } = this.state;
        const userId = lockScreenStore.selectedUserId;
        if (!userId || pin.length !== 4) return;
        this.setState({ submitting: true });
        const result = await lockScreenStore.unlock(userId, pin);
        if (!result.ok) {
            // brief shake feedback then clear
            this.setState({ pin: '', submitting: false });
        }
    }

    fullLogin = () => {
        route('/logout');
    }

    renderAvatar(u, size = 96) {
        return <Avatar name={fullNameOf(u)} image={u?.profileImage} faceBox={u?.profileImageFaceBox} size={size} />;
    }

    renderRoster() {
        const { roster } = lockScreenStore;
        const { isMobile } = this.state;
        const avatarSize = isMobile ? 56 : 96;
        const itemWidth = isMobile ? 96 : 140;
        const gap = isMobile ? '0.75rem' : '2.25rem';
        const nameMarginClass = isMobile ? 'mt-2' : 'mt-3';
        const nameFontSize = isMobile ? '0.8rem' : '1rem';
        const noPinFontSize = isMobile ? '0.7rem' : '0.8rem';
        return (
            <div class='d-flex flex-wrap justify-content-center' style={{ gap, maxWidth: '900px' }}>
                {roster.map(u => (
                    <button
                        type='button'
                        onClick={() => u.hasPin && this.selectUser(u.id)}
                        disabled={!u.hasPin}
                        class='border-0 bg-transparent d-flex flex-column align-items-center'
                        style={{
                            cursor: u.hasPin ? 'pointer' : 'not-allowed',
                            opacity: u.hasPin ? 1 : 0.45,
                            padding: '0.25rem',
                            width: `${itemWidth}px`,
                            transition: 'transform 0.15s ease',
                        }}
                        onMouseEnter={(e) => { if (u.hasPin) e.currentTarget.style.transform = 'scale(1.04)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                    >
                        {this.renderAvatar(u, avatarSize)}
                        <div
                            class={`${nameMarginClass} fw-medium text-center`}
                            style={{ fontSize: nameFontSize, lineHeight: 1.15, wordBreak: 'break-word' }}
                        >
                            {u.firstname} {u.lastname}
                        </div>
                        {!u.hasPin && (
                            <small
                                class='text-body-secondary mt-1 text-center'
                                style={{ fontSize: noPinFontSize, lineHeight: 1.15 }}
                            >
                                <Text id='lockScreen.noPin'>No PIN</Text>
                            </small>
                        )}
                    </button>
                ))}
            </div>
        );
    }

    renderKeypad() {
        const { roster, selectedUserId, pinError } = lockScreenStore;
        const user = roster.find(u => u.id === selectedUserId);
        if (!user) return null;
        const filled = this.state.pin.length;
        const dotSize = 16;
        const dotGap = 22;
        return (
            <div class='d-flex flex-column align-items-center'>
                {this.renderAvatar(user, 96)}
                <div class='mt-3 fs-5 fw-medium'>{user.firstname} {user.lastname}</div>
                <div class='text-body-secondary' style={{ fontSize: '0.95rem', marginTop: '0.25rem' }}>
                    <Text id='lockScreen.enterPin'>Enter PIN</Text>
                </div>

                <div
                    class='d-flex justify-content-center'
                    style={{
                        gap: `${dotGap}px`,
                        marginTop: '1.75rem',
                        marginBottom: pinError ? '1rem' : '2rem',
                        animation: pinError ? 'lockScreenShake 0.4s' : 'none',
                    }}
                >
                    {[0, 1, 2, 3].map(i => (
                        <div
                            style={{
                                width: `${dotSize}px`,
                                height: `${dotSize}px`,
                                borderRadius: '50%',
                                border: '1.5px solid var(--bs-body-color)',
                                background: i < filled ? 'var(--bs-body-color)' : 'transparent',
                                transition: 'background 0.1s ease',
                            }}
                        />
                    ))}
                </div>

                {pinError && (
                    <div class='text-danger mb-3' style={{ fontSize: '0.95rem' }}>
                        <Text id={`lockScreen.error.${pinError}`}>{pinError}</Text>
                    </div>
                )}

                <div
                    class='d-grid'
                    style={{
                        gridTemplateColumns: 'repeat(3, 72px)',
                        rowGap: '14px',
                        columnGap: '20px',
                    }}
                >
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(d => (
                        <button
                            type='button'
                            onClick={() => this.onPinDigit(d)}
                            class='border-0 d-flex align-items-center justify-content-center'
                            style={{
                                width: '72px',
                                height: '72px',
                                borderRadius: '50%',
                                background: 'rgba(127,127,127,0.12)',
                                fontSize: '1.6rem',
                                fontWeight: 300,
                                color: 'var(--bs-body-color)',
                                transition: 'background 0.1s ease',
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(127,127,127,0.22)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(127,127,127,0.12)'; }}
                        >
                            {d}
                        </button>
                    ))}
                    {/* Cancel — back to roster */}
                    <button
                        type='button'
                        onClick={() => { lockScreenStore.selectUser(null); this.setState({ pin: '' }); }}
                        class='border-0 d-flex align-items-center justify-content-center bg-transparent text-body-secondary'
                        style={{ width: '72px', height: '72px', fontSize: '0.9rem' }}
                    >
                        <Text id='lockScreen.cancel'>Cancel</Text>
                    </button>
                    <button
                        type='button'
                        onClick={() => this.onPinDigit(0)}
                        class='border-0 d-flex align-items-center justify-content-center'
                        style={{
                            width: '72px',
                            height: '72px',
                            borderRadius: '50%',
                            background: 'rgba(127,127,127,0.12)',
                            fontSize: '1.6rem',
                            fontWeight: 300,
                            color: 'var(--bs-body-color)',
                            transition: 'background 0.1s ease',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(127,127,127,0.22)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(127,127,127,0.12)'; }}
                    >
                        0
                    </button>
                    <button
                        type='button'
                        onClick={this.onBackspace}
                        class='border-0 d-flex align-items-center justify-content-center bg-transparent text-body-secondary'
                        style={{ width: '72px', height: '72px', fontSize: '1.2rem' }}
                        disabled={filled === 0}
                    >
                        <i class='fa-regular fa-delete-left' />
                    </button>
                </div>
            </div>
        );
    }

    render() {
        if (!lockScreenStore.isLocked) return null;
        const inKeypad = !!lockScreenStore.selectedUserId;
        const { isMobile } = this.state;
        return (
            <div
                class='position-fixed top-0 start-0 w-100 h-100 d-flex flex-column align-items-center'
                style={{
                    zIndex: 9999,
                    background: 'var(--bs-body-bg)',
                    paddingTop: isMobile ? '3vh' : '8vh',
                    paddingBottom: isMobile ? '2vh' : '4vh',
                    overflowY: 'auto',
                    WebkitOverflowScrolling: 'touch',
                }}
            >
                <style>{`
                    @keyframes lockScreenShake {
                        0%, 100% { transform: translateX(0); }
                        20% { transform: translateX(-8px); }
                        40% { transform: translateX(8px); }
                        60% { transform: translateX(-5px); }
                        80% { transform: translateX(5px); }
                    }
                `}</style>

                <div
                    class='text-body-secondary text-uppercase'
                    style={{ fontSize: '0.75rem', letterSpacing: '0.18em', marginBottom: '0.5rem' }}
                >
                    <i class='fa-regular fa-lock me-2' />
                    <Text id='lockScreen.locked'>Locked</Text>
                </div>
                <h1
                    class={`fw-light ${isMobile ? 'mb-3' : 'mb-5'}`}
                    style={{ fontSize: isMobile ? '1.4rem' : '2rem', letterSpacing: '-0.01em' }}
                >
                    {inKeypad
                        ? <Text id='lockScreen.titleKeypad'>Enter your PIN</Text>
                        : <Text id='lockScreen.title'>Pick your name</Text>}
                </h1>

                <div class='flex-grow-1 d-flex align-items-center justify-content-center w-100'>
                    {inKeypad ? this.renderKeypad() : this.renderRoster()}
                </div>

                {inKeypad && (
                    <div class='text-body-secondary mt-4 text-center' style={{ fontSize: '0.85rem', maxWidth: '360px' }}>
                        <Text id='lockScreen.forgotPinHint'>Forgot your PIN? Use full login below. Right after logging in you can set a new PIN from the menu.</Text>
                    </div>
                )}

                <button
                    type='button'
                    onClick={this.fullLogin}
                    class='border-0 bg-transparent text-body-secondary mt-4'
                    style={{ fontSize: '0.95rem', padding: '0.5rem 1rem' }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--bs-body-color)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = ''; }}
                >
                    <i class='fa-regular fa-right-from-bracket me-2' />
                    <Text id='lockScreen.useFullLogin'>Use full login</Text>
                </button>
            </div>
        );
    }
}

export default LockScreen;
